import { useExtractionJobs } from '@ctw/shared/api/ai/use-extraction-jobs';
import { Button } from '@ctw/shared/components/button';
import { withErrorBoundary } from '@ctw/shared/components/errors/error-boundary';
import { SelectInput } from '@ctw/shared/components/form/select-input';
import { ProgressBar } from '@ctw/shared/components/indicators/progress-bar';
import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import { notify } from '@ctw/shared/components/toast';
import { useOpenSourceDocument } from '@ctw/shared/content/CCDA/use-open-source-document';
import {
  buildSpecimenOptions,
  mapExtractionJob,
} from '@ctw/shared/content/fact-extraction/map-extraction-job';
import {
  type PatientFact,
  PatientFactExtractionGroupedTable,
  type PatientFactGroup,
} from '@ctw/shared/content/fact-extraction/patient-fact-extraction-grouped-table';
import {
  type CitationSource,
  useCitationSources,
} from '@ctw/shared/content/fact-extraction/use-citation-sources';
import { usePatientContext } from '@ctw/shared/context/patient-provider';
import { useResourceDrawer } from '@ctw/shared/context/resource-drawer-context';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { tw } from '@ctw/shared/utils/tailwind';
import { faDownload } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMemo, useState } from 'react';

export interface PatientFactExtractionProps {
  showExport?: boolean;
}

export const PatientFactExtraction = withErrorBoundary({
  boundaryName: 'PatientFactExtraction',
  Component: ({ showExport = false }: PatientFactExtractionProps) => {
    const { patient } = usePatientContext();
    const { data: jobs = [], isLoading } = useExtractionJobs(patient.UPID);

    const [selectedJobId, setSelectedJobId] = useState<string | undefined>(undefined);

    const specimenOptions = useMemo(() => buildSpecimenOptions(jobs), [jobs]);
    const selectedJob = useMemo(
      () => jobs.find((job) => job.id === selectedJobId) ?? jobs[0],
      [jobs, selectedJobId],
    );

    const mapped = useMemo(
      () => (selectedJob ? mapExtractionJob(selectedJob) : undefined),
      [selectedJob],
    );

    const { sourceByCitationId } = useCitationSources(selectedJob?.attributes.citations ?? []);
    const { openSourceDocumentById } = useOpenSourceDocument();
    const { openResourceDrawer } = useResourceDrawer();
    const telemetry = useTelemetry();

    const { factGroups, sourceByFactId } = useMemo(() => {
      const sourceById = new Map<string, CitationSource>();
      const groups: Array<PatientFactGroup> = (mapped?.groups ?? []).map((group) => ({
        label: group.label,
        facts: group.facts.map((fact): PatientFact => {
          if (!fact.isDocumented) {
            return { id: fact.id, label: fact.label };
          }
          if (fact.citationIds.length === 0) {
            return { id: fact.id, label: fact.label, value: fact.value };
          }
          const openableCitationId = fact.citationIds.find((citationId) =>
            sourceByCitationId.has(citationId),
          );
          const source =
            openableCitationId !== undefined
              ? sourceByCitationId.get(openableCitationId)
              : undefined;
          if (source) {
            sourceById.set(fact.id, source);
          }
          return {
            id: fact.id,
            label: fact.label,
            value: fact.value,
            citation: { isOpenable: Boolean(source), label: source?.label },
          };
        }),
      }));
      return { factGroups: groups, sourceByFactId: sourceById };
    }, [mapped, sourceByCitationId]);

    if (isLoading) {
      return <LoadingSpinner centered={true} message="Loading extracted facts..." />;
    }

    if (!(selectedJob && mapped)) {
      return (
        <div className={tw`px-2 py-6 text-center font-medium text-content-subtle text-sm`}>
          No extracted facts are available for this patient yet.
        </div>
      );
    }

    const formattedDate = mapped.createdAt
      ? mapped.createdAt.toLocaleDateString('en-US')
      : undefined;

    return (
      <div className={tw`flex flex-col gap-4 px-2 py-1`}>
        <div className={tw`flex flex-row flex-wrap items-center justify-between gap-2`}>
          <SelectInput
            name="specimen"
            label="Specimen:"
            labelPosition="left"
            options={specimenOptions}
            initialValue={selectedJob.id}
            onChange={(option) => setSelectedJobId(option?.value)}
            inputClassName={tw`min-w-60 max-w-70`}
            labelClassName={tw`font-semibold text-content-subtle text-sm`}
          />
          <div className={tw`flex shrink-0 flex-row items-center justify-end gap-2`}>
            {formattedDate && (
              <div className={tw`whitespace-nowrap font-medium text-content-subtle text-xs`}>
                {formattedDate}
              </div>
            )}
            <div
              className={tw`whitespace-nowrap rounded-sm bg-background-disabled p-1 font-medium text-content-subtle text-xs`}
            >
              {mapped.fieldsTotal} fields in export
            </div>
            {showExport && (
              <Button
                type="button"
                variant="primary"
                size="xs"
                onClick={() => notify({ type: 'info', title: 'Not implemented yet' })}
                className={tw`py-1.5`}
              >
                <div className={tw`flex flex-nowrap items-center space-x-2`}>
                  <FontAwesomeIcon icon={faDownload} className={tw`h-4 w-4`} />
                  <div className={tw`whitespace-nowrap font-bold text-sm`}>Export</div>
                </div>
              </Button>
            )}
          </div>
        </div>
        <div
          className={tw`flex flex-row flex-wrap items-center justify-between gap-3 rounded-md border border-border-main px-4 py-3`}
        >
          <div className={tw`flex flex-row flex-wrap items-center gap-3`}>
            <div className={tw`whitespace-nowrap font-semibold text-content-main text-sm`}>
              {mapped.fieldsExtracted}/{mapped.fieldsTotal} fields extracted
            </div>
            <div className={tw`w-32`}>
              <ProgressBar
                label="Fields extracted"
                labelVisuallyHidden={true}
                value={mapped.fieldsExtracted}
                max={mapped.fieldsTotal}
                valueText={`${mapped.fieldsExtracted} of ${mapped.fieldsTotal} fields extracted`}
              />
            </div>
          </div>
        </div>
        <PatientFactExtractionGroupedTable
          factGroups={factGroups}
          onOpenCitation={({ fact }) => {
            const source = sourceByFactId.get(fact.id);
            if (!source) {
              return;
            }
            if (source.kind === 'document') {
              void openSourceDocumentById(source.binaryId);
            } else {
              openResourceDrawer({ telemetry, model: source.model as never });
            }
          }}
        />
      </div>
    );
  },
});
